import { useMemo } from 'react';
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import qs from 'qs';
import { datadogLogs } from '@datadog/browser-logs';

import {
    ApiError,
    ArgumentUsedError,
    NonEmptyItemOverwrite,
    NoPolicyError,
    SchemaError,
    StaleTicketUpdate,
    TemplateError,
    TicketIsLockedError,
} from './error';

// Should be used outside React context only (before app is initialized). Use `useApi` hook when possible.
export const api = buildApi();

export const useApi = () => {
    return useMemo(buildApi, []);
};

function buildApi() {
    const instance = axios.create({
        baseURL: import.meta.env.VITE_API_URL,
        paramsSerializer(params) {
            return qs.stringify(params, { indices: false, arrayFormat: 'repeat' });
        },
    });

    instance.interceptors.request.use(async function (request: InternalAxiosRequestConfig) {
        if (!('Authorization' in request.headers)) {
            request.headers.Authorization = await getJwtToken();
        }
        return request;
    });

    instance.interceptors.response.use(
        (response: AxiosResponse) => {
            const data = response.data;
            return data && Object.prototype.hasOwnProperty.call(data, 'results') ? data.results : data;
        },
        async (error: AxiosError) => {
            const requestDetails = error.config
                ? {
                      url: error.config.url,
                      method: error.config.method,
                      headers: error.config.headers,
                      data: error.config.data,
                      timeout: error.config.timeout,
                      baseURL: error.config.baseURL,
                      params: error.config.params,
                  }
                : {};

            datadogLogs.logger.warn('API Error', {
                error,
                request: requestDetails,
            });
            if (error.response?.status === 422) {
                throw new SchemaError('Bad schema', error.response.data);
            }

            if (error.response?.status >= 400 && error.response?.data) {
                switch (error.response.data.code) {
                    case 'NO_POLICY':
                        throw new NoPolicyError(error.response.data.detail);
                    case 'STALE_TICKET_UPDATE':
                        throw new StaleTicketUpdate(error.response.data.detail);
                    case 'ARGUMENT_METADATA_IN_USE':
                        throw new ArgumentUsedError('Argument metadata is being used', error.response.data.detail);
                    case 'TEMPLATE_ERROR':
                        throw new TemplateError('Template error', error.response.data.detail);
                    case 'NON_EMPTY_ITEM_OVERWRITE':
                        throw new NonEmptyItemOverwrite(error.response.data.detail);
                    case 'TICKET_IS_LOCKED':
                        throw new TicketIsLockedError(error.response.data.detail);
                    default:
                        throw new ApiError(error.response.data.code, 'Backend error', error.response.data.detail);
                }
            }
            throw error;
        },
    );

    return instance;
}

async function getJwtToken() {
    try {
        const cognitoTokens = (await fetchAuthSession()).tokens;
        return cognitoTokens?.idToken?.toString();
    } catch {
        return Promise.reject(() => window.location.reload());
    }
}
